import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import axios from "axios";
import { FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
    EntryField,
    EntryTitle,
    EntryWrapper,
    FormWrapper,
} from "../../../components/customStyles/form";
import Progress from "../../../components/progress/Progress";
import { ERole } from "../../../models/enums/role";
import IUser from "../../../models/user";
import AuthService from "../../../services/auth/AuthService";
import { default as PropertyGroupService } from "../../../services/propertyGroupService/PropertyGroupService";
import UserService from "../../../services/user/UserService";
import { DEFAULT_ROUNDING_SETTINGS, IEmployeeBreakRule, ITimeRoundingSettings, RoundingDirection } from "../../../models/propertyGroup";
import { theme } from "../../../styles/themes/mainTheme";
import CloseIcon from "@mui/icons-material/Close";
import { HOURS_WORKED_MIN_VALUE, BREAK_MINUTES_MAX_VALUE, BREAK_MINUTES_MIN_VALUE, HOURS_WORKED_MAX_VALUE } from "./PropertyGroupEditPage";
import EditOnly from "../../../components/permissions/EditOnly";


const DEFAULT_INACTIVITY_THREE_HOURS = 180;

export interface IBuilding {
    id: number;
    name: string;
    isAlreadyAssigned: boolean;
}

export interface IDuplicateCode {
    message: string;
}
interface IPropertyGroupFormState {
    name: string;
    scanInactivityTimeout: number;
    selectedBuildingsIds: number[];
    codes: string[];
    codeInput: string;
    errorCodes: string | null;
    isSelfieRequired: boolean;
    punchDeviceId: string;
    acombaProjectId?: number;
    isBreakDeductionEnabled: boolean;
    employeeBreakRules: IEmployeeBreakRule[];
    timeRoundingSettings?: ITimeRoundingSettings;
}

const PropertyGroupAddPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isFormSending, setIsFormSending] = useState(false);
    const [errorCodes, setErrorCodes] = useState<string | null>(null);
    const [formState, setFormState] = useState<IPropertyGroupFormState>({
        name: "",
        selectedBuildingsIds: [],
        codes: [],
        codeInput: "",
        errorCodes: null,
        scanInactivityTimeout: DEFAULT_INACTIVITY_THREE_HOURS,
        isSelfieRequired: false,
        punchDeviceId: "",
        acombaProjectId: undefined,
        isBreakDeductionEnabled: false,
        employeeBreakRules: [],
        timeRoundingSettings: undefined
    });
    const [userInput, setUserInput] = useState("");
    const [availableUsers, setAvailableUsers] = useState<IUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);

    useEffect(() => {
        UserService.GetUsersByCompany().then((fetchedUsers) => {
            setAvailableUsers(fetchedUsers);

            const user = AuthService.user;
            if (!user) return;
            setSelectedUsers([user]);
        });
    }, []);

    const onCreatePropertyGroup = async (event: FormEvent) => {
        event.preventDefault();
        setIsFormSending(true);
        setErrorCodes(null);

        try {
            const propertyGroupId = await PropertyGroupService.PostPropertyGroup({
                name: formState.name,
                buildingsIds: formState.selectedBuildingsIds,
                codes: formState.codes,
                scanInactivityTimeout: formState.scanInactivityTimeout,
                isSelfieRequired: formState.isSelfieRequired,
                punchDeviceId: formState.punchDeviceId,
                userIds: selectedUsers.map(user => user.id),
                acombaProjectId: formState.acombaProjectId || undefined,
                isBreakDeductionEnabled: formState.isBreakDeductionEnabled,
                employeeBreakRules: formState.employeeBreakRules,
                timeRoundingSettings: formState.timeRoundingSettings,
            });
            if (propertyGroupId !== 0) {
                navigate("..");
            }
        } catch (error) {
            if (!axios.isAxiosError(error)) return;
            const errorResponse = error.response?.data.message as IDuplicateCode[];
            if (errorResponse) setErrorCodes(`${errorResponse.map(item => item.message).join('\n')} `);
        } finally {
            setIsFormSending(false);
        }

    };

    const onAddCode = (value: string) => {
        const trimmedValue = value.trim();
        if (!trimmedValue || formState.codes.includes(trimmedValue)) return;

        setFormState(prev => ({
            ...prev,
            codes: [...prev.codes, trimmedValue],
            codeInput: "",
        }));
    };

    const isFormValid = () => {
        if (!formState.name.trim()) return false;
        if (!formState.scanInactivityTimeout || formState.scanInactivityTimeout < 1) return false;
        if (formState.isBreakDeductionEnabled && formState.employeeBreakRules.length === 0) return false;
        if (formState.employeeBreakRules.length && formState.employeeBreakRules.some(rule => rule.minHoursWorked < HOURS_WORKED_MIN_VALUE || rule.minHoursWorked > HOURS_WORKED_MAX_VALUE || rule.totalBreakMinutes < BREAK_MINUTES_MIN_VALUE || rule.totalBreakMinutes > BREAK_MINUTES_MAX_VALUE)) return false;
        const uniqueBreakRules = new Set(formState.employeeBreakRules.map(rule => rule.minHoursWorked));
        if (uniqueBreakRules.size !== formState.employeeBreakRules.length) return false;

        return true;
    };

    const addBreakRule = () => {
        const usedValues = formState.employeeBreakRules.map(r => r.minHoursWorked);
        let newMin = HOURS_WORKED_MIN_VALUE;
        while (usedValues.includes(newMin) && newMin <= HOURS_WORKED_MAX_VALUE) newMin++;

        setFormState(prev => ({
            ...prev,
            employeeBreakRules: [...prev.employeeBreakRules, { id: 0, propertyGroupId: 0, minHoursWorked: newMin, totalBreakMinutes: 15 }]
        }))
    };


    return (
        <Box component={Paper} sx={{ width: "100%" }}>
            <form autoComplete="off" onSubmit={onCreatePropertyGroup}>
                <FormWrapper>
                    {errorCodes && errorCodes.split('\n').map((line, index) => (
                        <Typography marginTop={2} key={index} variant="body2" color="error" >{line}</Typography>
                    ))
                    }

                    <EntryWrapper>
                        <EntryTitle>
                            {t("table.property.name")} *
                        </EntryTitle>
                        <EntryField>
                            <TextField
                                required={true}
                                style={{ textAlign: "left", width: "100%" }}
                                value={formState.name}
                                onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                                slotProps={{ htmlInput: { maxLength: 50 } }}

                            />
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>{t("table.user.users")}</EntryTitle>
                        <EntryField>
                            <Autocomplete
                                fullWidth
                                multiple
                                freeSolo={false}
                                options={availableUsers}
                                value={selectedUsers}
                                getOptionLabel={(option) => option.name}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                onChange={(_, newSelectedUsers) => {
                                    setSelectedUsers(newSelectedUsers);
                                }}
                                inputValue={userInput}
                                onInputChange={(_, value) => setUserInput(value)}
                                renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            <span>{option.name}</span>
                                            {option.role !== ERole.Employee && (
                                                <Chip label={t("table.user.Manager")} color="primary" size="small" />
                                            )}
                                        </div>
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={
                                                <span>
                                                    {option.name}
                                                    {option.role !== ERole.Employee && (
                                                        <span style={{ marginLeft: 6, fontSize: 10, background: '#1976d2', color: 'white', borderRadius: 100, padding: '2px 6px' }}>
                                                            {t("table.user.Manager").charAt(0).toUpperCase()}
                                                        </span>
                                                    )}
                                                </span>
                                            }
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                    />
                                )}
                            />

                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>Codes </EntryTitle>
                        <Box sx={{
                            display: "flex",
                            flex: 1,
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            alignItems: "left",
                            marginTop: "1em",
                        }}>
                            <Autocomplete
                                fullWidth
                                multiple
                                freeSolo
                                value={formState.codes}
                                onChange={(_, newCodes) => setFormState(prev => ({ ...prev, codes: newCodes }))}
                                inputValue={formState.codeInput}
                                onInputChange={(_, value) => {
                                    setFormState(prev => ({ ...prev, codeInput: value }));
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        onAddCode(formState.codeInput);
                                    }
                                }}
                                options={[]}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            label={option}
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        onBlur={(event) => {
                                            onAddCode(event.target.value);
                                        }}
                                        InputLabelProps={{
                                            children: null,
                                        }}
                                    />
                                )}
                            />

                        </Box>

                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>
                            {t("table.property.scanInactivity")} *
                        </EntryTitle>
                        <EntryField>
                            <TextField
                                type="number"
                                required={true}
                                style={{ textAlign: "left", width: "100%" }}
                                value={formState.scanInactivityTimeout}
                                onChange={(e) => setFormState(prev => ({ ...prev, scanInactivityTimeout: Number(e.target.value) }))}
                                slotProps={{ htmlInput: { maxLength: 50, min: 1 } }}
                            />
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>{t("table.property.isSelfieRequired")} </EntryTitle>

                        <EntryField>
                            <Checkbox
                                defaultChecked={false}
                                checked={formState.isSelfieRequired}
                                onChange={(e) => setFormState(prev => ({ ...prev, isSelfieRequired: !prev.isSelfieRequired }))}

                            />
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>
                            {t("table.property.punchDeviceId")}
                        </EntryTitle>
                        <EntryField>
                            <TextField
                                style={{ textAlign: "left", width: "100%" }}
                                value={formState.punchDeviceId}
                                onChange={(e) => {
                                    setFormState(prev => ({ ...prev, punchDeviceId: e.target.value }));
                                }}
                                slotProps={{ htmlInput: { maxLength: 45 } }}
                            />
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>
                            {t("table.property.acombaProjectId")}
                        </EntryTitle>
                        <EntryField>
                            <TextField
                                type="number"
                                style={{ textAlign: "left", width: "100%" }}
                                value={formState.acombaProjectId || undefined}
                                onChange={(e) => {
                                    setFormState(prev => ({ ...prev, acombaProjectId: Number(e.target.value) || undefined }));
                                }}
                                slotProps={{ htmlInput: { min: 1 } }}
                            />
                        </EntryField>
                    </EntryWrapper>

                    {/* <EntryWrapper>
                            <EntryTitle>{t("table.property.breakDeduction")}</EntryTitle>
                            <EntryField>
                                <Checkbox
                                    checked={formState.isBreakDeductionEnabled}
                                    onChange={() => setFormState(prev => ({ ...prev, isBreakDeductionEnabled: !prev.isBreakDeductionEnabled }))}
                                />
                            </EntryField>
                        </EntryWrapper>

                        {formState.isBreakDeductionEnabled && (
                            <Box sx={{ borderLeft: '3px solid #1976d2', paddingLeft: 2 }}>
                                <Typography variant="body1" fontWeight="bold">{t("table.property.breakRules")}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
                                    <Trans i18nKey="table.property.breakRulesCaption" components={{ 1: <strong />, 3: <strong /> }} />
                                </Typography>

                                {formState.employeeBreakRules?.map((rule, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                                        <Typography variant="body2">{t("table.property.ruleText1")}</Typography>
                                        <TextField
                                            label={t("globalDashboard.hours")}
                                            type="number"
                                            value={rule.minHoursWorked}
                                            slotProps={{ htmlInput: { min: HOURS_WORKED_MIN_VALUE, max: HOURS_WORKED_MAX_VALUE } }}
                                            error={formState.employeeBreakRules.some((r, i) => i !== index && r.minHoursWorked === rule.minHoursWorked) || rule.minHoursWorked < HOURS_WORKED_MIN_VALUE || rule.minHoursWorked > HOURS_WORKED_MAX_VALUE}
                                            onChange={(e) => {
                                                const updated = [...formState.employeeBreakRules];
                                                updated[index].minHoursWorked = parseFloat(e.target.value);
                                                setFormState(prev => ({ ...prev, employeeBreakRules: updated }));
                                            }}
                                        />
                                        <Typography variant="body2">{t("table.property.ruleText2")}</Typography>
                                        <TextField
                                            label={t("globalDashboard.minutes")}
                                            type="number"
                                            value={rule.totalBreakMinutes}
                                            slotProps={{ htmlInput: { min: BREAK_MINUTES_MIN_VALUE, max: BREAK_MINUTES_MAX_VALUE } }}
                                            error={rule.totalBreakMinutes < BREAK_MINUTES_MIN_VALUE || rule.totalBreakMinutes > BREAK_MINUTES_MAX_VALUE}
                                            onChange={(e) => {
                                                const updated = [...formState.employeeBreakRules];
                                                updated[index].totalBreakMinutes = parseInt(e.target.value);
                                                setFormState(prev => ({ ...prev, employeeBreakRules: updated }));
                                            }}
                                        />
                                        <Typography variant="body2">{t("table.property.ruleText3")}</Typography>
                                        <IconButton
                                            onClick={() => {
                                                const updated = formState.employeeBreakRules.filter((_, i) => i !== index);
                                                setFormState(prev => ({ ...prev, employeeBreakRules: updated }));
                                            }}
                                            sx={{ color: theme.palette.error.main }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}

                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        variant="outlined"
                                        disabled={formState.employeeBreakRules.length >= HOURS_WORKED_MAX_VALUE}
                                        onClick={() => addBreakRule()}
                                    >
                                        {t("table.property.addRule")}
                                    </Button>
                                </Box>
                            </Box>
                        )} */}

                    {/* <EntryWrapper>
                            <EntryTitle>{t("table.property.rounding")}</EntryTitle>
                            <EntryField>
                                <Checkbox
                                    checked={formState.timeRoundingSettings?.isEnabled}
                                    onChange={() =>
                                        setFormState((prev) => {
                                            const isEnabled = !prev.timeRoundingSettings?.isEnabled;
                                            return {
                                                ...prev,
                                                timeRoundingSettings: isEnabled
                                                    ? DEFAULT_ROUNDING_SETTINGS
                                                    : undefined
                                            };
                                        })
                                    }
                                />
                            </EntryField>
                        </EntryWrapper>

                        {formState.timeRoundingSettings && formState.timeRoundingSettings.isEnabled && (
                            <Box sx={{ borderLeft: '3px solid #1976d2', pl: 2, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant="body1" fontWeight="bold">{t("table.property.roundingRules")}</Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">{t("table.property.roundingDirection")}</Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={formState.timeRoundingSettings.direction}
                                        onChange={(e) =>
                                            setFormState((prev) => {
                                                if (!prev.timeRoundingSettings) return prev;
                                                return {
                                                    ...prev,
                                                    timeRoundingSettings: {
                                                        ...prev.timeRoundingSettings,
                                                        direction: parseInt(e.target.value) as RoundingDirection
                                                    }
                                                };
                                            })
                                        }
                                    >
                                        <MenuItem value={RoundingDirection.UP}>{t("table.property.roundUp")}</MenuItem>
                                        <MenuItem value={RoundingDirection.DOWN}>{t("table.property.roundDown")}</MenuItem>
                                        <MenuItem value={RoundingDirection.NEAREST}>{t("table.property.roundNearest")}</MenuItem>
                                    </TextField>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">{t("table.property.roundingInterval")}</Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={formState.timeRoundingSettings.interval}
                                        onChange={(e) =>
                                            setFormState((prev) => {
                                                if (!prev.timeRoundingSettings) return prev;
                                                return {
                                                    ...prev,
                                                    timeRoundingSettings: {
                                                        ...prev.timeRoundingSettings,
                                                        interval: parseInt(e.target.value),
                                                    }
                                                };
                                            })
                                        }
                                    >
                                        {[5, 10, 15, 30].map((min) => (
                                            <MenuItem key={min} value={min}>
                                                {min} {t("globalDashboard.minutes")}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            </Box>
                        )} */}


                    <EntryWrapper
                        sx={{
                            justifyContent: "flex-end",
                            marginBlock: "1em",
                        }}
                    >

                        {(
                            <EntryWrapper sx={{ justifyContent: "flex-end" }}>
                                <Progress isLoading={isFormSending}>
                                    <EditOnly>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            disabled={!isFormValid() || isFormSending}
                                            sx={{
                                                marginTop: "4em",
                                            }}
                                        >
                                            {t("table.property.create")}
                                        </Button>
                                    </EditOnly>
                                </Progress>
                            </EntryWrapper>
                        )}
                    </EntryWrapper>
                </FormWrapper>
            </form>
        </Box>
    );
};

export default PropertyGroupAddPage;
